(() => {
  const shellSelector = '.arista-shell';
  const shellObservers = new WeakMap();
  const drawerLoopCleanups = new WeakMap();
  const topLevelDisclosureSelector =
    '.arista-shell__desktop-nav > .arista-shell__desktop-nav-list > .arista-shell__nav-item > header-menu > details';

  const initialiseShell = (shell) => {
    if (!(shell instanceof HTMLElement) || shell.dataset.aristaShellReady === 'true') return;

    shell.dataset.aristaShellReady = 'true';
    const disclosures = Array.from(shell.querySelectorAll(topLevelDisclosureSelector));

    disclosures.forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      if (!summary) return;

      const syncExpandedState = () => {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      };

      syncExpandedState();

      details.addEventListener('toggle', () => {
        syncExpandedState();
        if (!details.open) return;

        disclosures.forEach((otherDetails) => {
          if (otherDetails !== details && otherDetails.open) otherDetails.open = false;
        });
      });

      details.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !details.open) return;

        details.open = false;
        summary.focus();
        event.stopPropagation();
      });
    });

    const drawerVideos = [...shell.querySelectorAll('[data-arista-drawer-loop]')];
    const videoCleanups = [];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const saveData = Boolean(navigator.connection?.saveData);

    drawerVideos.forEach((video) => {
      const source = video.querySelector('source[data-src]');
      const details = video.closest('details');
      const requestedLoop = Number(video.dataset.loopSeconds || 2);
      const loopSeconds = Number.isFinite(requestedLoop) ? Math.max(1, Math.min(2, requestedLoop)) : 2;

      const resetToPoster = () => {
        video.pause();
        try {
          video.currentTime = 0;
        } catch (_error) {
          // Metadata may not be ready yet; the poster remains visible.
        }
      };

      const syncPlayback = () => {
        if (!details?.open || reducedMotion.matches || saveData) {
          resetToPoster();
          return;
        }
        if (source?.dataset.src && !source.src) {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
          video.load();
        }
        const playPromise = video.play();
        playPromise?.catch(() => resetToPoster());
      };

      const handleTimeUpdate = () => {
        if (video.currentTime < loopSeconds) return;
        video.currentTime = 0;
        if (details?.open && !reducedMotion.matches && !saveData) video.play()?.catch(() => resetToPoster());
      };

      const handleMotionChange = () => syncPlayback();
      details?.addEventListener('toggle', syncPlayback);
      video.addEventListener('timeupdate', handleTimeUpdate);
      if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', handleMotionChange);
      else reducedMotion.addListener(handleMotionChange);

      videoCleanups.push(() => {
        resetToPoster();
        details?.removeEventListener('toggle', syncPlayback);
        video.removeEventListener('timeupdate', handleTimeUpdate);
        if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', handleMotionChange);
        else reducedMotion.removeListener(handleMotionChange);
      });
    });

    if (videoCleanups.length) drawerLoopCleanups.set(shell, () => videoCleanups.forEach((cleanup) => cleanup()));

    const section = shell.closest('.section-header');
    if (!section || !('ResizeObserver' in window)) return;

    const observer = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--arista-shell-header-height', `${Math.round(section.offsetHeight)}px`);
    });

    observer.observe(section);
    shellObservers.set(shell, observer);
  };

  const initialiseAllShells = (scope = document) => {
    if (scope instanceof HTMLElement && scope.matches(shellSelector)) initialiseShell(scope);
    scope.querySelectorAll?.(shellSelector).forEach(initialiseShell);
  };

  const syncFloatingActionVisibility = () => {
    const shouldHide = Boolean(document.querySelector('main[data-template="collection"], arista-bogo-builder'));
    document.body?.classList.toggle('arista-no-floating-actions', shouldHide);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialiseAllShells();
      syncFloatingActionVisibility();
    }, { once: true });
  } else {
    initialiseAllShells();
    syncFloatingActionVisibility();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseAllShells(event.target);
    syncFloatingActionVisibility();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const shell = event.target.matches?.(shellSelector) ? event.target : event.target.querySelector?.(shellSelector);
    if (!shell) return;
    drawerLoopCleanups.get(shell)?.();
    drawerLoopCleanups.delete(shell);
    shellObservers.get(shell)?.disconnect();
    shellObservers.delete(shell);
  });
})();
